import React, { useState, useEffect } from 'react';
import { Star, UserPlus, X, ClipboardList, ChevronDown, MessageCircle, Share2 } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

// Available duties that can be assigned
const AVAILABLE_DUTIES = [
  { id: 'none', label: 'No Duty', icon: '' },
  { id: 'ground_setup', label: 'Ground Setup', icon: String.fromCodePoint(0x1F3DF, 0xFE0F) },
  { id: 'refreshments', label: 'Drinks & Snacks', icon: String.fromCodePoint(0x1F964) },
  { id: 'scoring', label: 'Scoring', icon: String.fromCodePoint(0x1F4DD) },
  { id: 'umpiring', label: 'Umpiring', icon: String.fromCodePoint(0x1F9D1, 0x200D, 0x2696, 0xFE0F) },
  { id: 'first_aid', label: 'First Aid', icon: String.fromCodePoint(0x1FA79) },
  { id: 'equipment', label: 'Equipment', icon: String.fromCodePoint(0x1F392) },
  { id: 'transport', label: 'Transport', icon: String.fromCodePoint(0x1F697) },
  { id: 'photography', label: 'Photography', icon: String.fromCodePoint(0x1F4F8) },
];

const SquadSelection = ({
  game,
  tournament, // Tournament with ball type and squad settings
  team, // Team as fallback for ball type
  availablePlayers,
  expenses = [], // Club expenses including Initial Donations
  walletTransactions = [], // Wallet transactions for payment tracking
  canOverridePaymentBlock = false, // Captains/Vice-captains and Admins can override
  onBack,
  onSave
}) => {
  // Early return if no game
  if (!game) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">No game selected</p>
        <button onClick={onBack} className="mt-4 text-green-600 hover:underline">← Back</button>
      </div>
    );
  }

  // Priority: Game settings > Tournament settings > Team ball type > Defaults
  // Check if game has squad settings defined
  const gameHasSquadSettings = game.maxSquadSize !== undefined && game.maxSquadSize !== null;

  // Determine ball type: tournament > team > default
  const ballType = tournament?.ballType || team?.ballType || 'leather';

  // Determine squad settings with priority: tournament (if game belongs to one) > game > defaults
  const isCustomMode = tournament
    ? tournament.maxSquadSize === 0
    : (gameHasSquadSettings
      ? game.maxSquadSize === 0
      : false);

  const maxSquadSize = isCustomMode
    ? 999
    : (tournament
      ? (tournament.maxSquadSize || (ballType === 'tennis' ? 8 : 11))
      : (gameHasSquadSettings
        ? game.maxSquadSize
        : (ballType === 'tennis' ? 8 : 11)));

  const allowSuperSubs = isCustomMode
    ? false
    : (tournament
      ? !!tournament.allowSuperSubs
      : (game.allowSuperSubs || false));

  const minSquadSize = isCustomMode ? 1 : (ballType === 'tennis' ? 6 : 9); // Minimum players needed

  // Helper: Check if player is available for this game
  const isPlayerAvailable = (player) => {
    if (!player) return false;
    const idToCheck = player.userId || player.id;
    const availability = game.availability?.[idToCheck] || game.availability?.[player.id];
    return availability?.status === 'available';
  };

  const [selectedPlayers, setSelectedPlayers] = useState(game.playing11 || []);
  const [superSubs, setSuperSubs] = useState(game.superSubs || []);
  const [playerDuties, setPlayerDuties] = useState(game.playerDuties || {}); // { playerId: 'duty_id' }
  const [guestPlayers, setGuestPlayers] = useState(game.guestPlayers || []); // Load existing guest players from game
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [openDutyDropdown, setOpenDutyDropdown] = useState(null); // Track which dropdown is open
  const [isSaving, setIsSaving] = useState(false); // Track save state
  const [showPublishModal, setShowPublishModal] = useState(false); // Track if modal is open
  const [publishOption, setPublishOption] = useState('impacted_only'); // Default to impacted only

  const togglePlayer = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      // Remove player and also remove from super subs and duties if they were marked
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
      setSuperSubs(superSubs.filter(id => id !== playerId));
      const newDuties = { ...playerDuties };
      delete newDuties[playerId];
      setPlayerDuties(newDuties);
    } else {
      // Add player (soft limit - just warn if over)
      setSelectedPlayers([...selectedPlayers, playerId]);
    }
  };

  const toggleSuperSub = (playerId) => {
    // Can only mark as super sub if player is already selected and super subs allowed
    if (!selectedPlayers.includes(playerId) || !allowSuperSubs) return;

    if (superSubs.includes(playerId)) {
      // Remove from super subs
      setSuperSubs(superSubs.filter(id => id !== playerId));
    } else if (superSubs.length < 2) {
      // Add to super subs (max 2)
      setSuperSubs([...superSubs, playerId]);
    }
  };

  const assignDuty = (playerId, dutyId) => {
    if (dutyId === 'none') {
      const newDuties = { ...playerDuties };
      delete newDuties[playerId];
      setPlayerDuties(newDuties);
    } else {
      setPlayerDuties({ ...playerDuties, [playerId]: dutyId });
    }
    setOpenDutyDropdown(null);
  };

  const handleAddGuest = () => {
    if (!guestName.trim()) {
      alert('Please enter guest player name');
      return;
    }

    const guestPlayer = {
      id: `guest-${Date.now()}`,
      name: guestName.trim(),
      email: guestEmail.trim() || 'guest@temp.com',
      isGuest: true,
      isExternal: true // External player - no Firebase account, needs manual payment tracking
    };

    // Add guest (soft limit - just warn if over)
    setGuestPlayers([...guestPlayers, guestPlayer]);
    setSelectedPlayers([...selectedPlayers, guestPlayer.id]);
    setGuestName('');
    setGuestEmail('');
    setShowAddGuest(false);
  };

  const handleRemoveGuest = (guestId) => {
    setGuestPlayers(guestPlayers.filter(g => g.id !== guestId));
    setSelectedPlayers(selectedPlayers.filter(id => id !== guestId));
    setSuperSubs(superSubs.filter(id => id !== guestId));
    const newDuties = { ...playerDuties };
    delete newDuties[guestId];
    setPlayerDuties(newDuties);
  };

  const handleSave = async (isPublish = false, overrideNotificationOption = null) => {
    if (isSaving) return;

    // Cannot publish an empty squad
    if (isPublish && selectedPlayers.length === 0) {
      alert('Please select at least one player to publish');
      return;
    }

    // Soft warning for squad size (not in custom mode)
    if (!isCustomMode && isPublish) {
      if (selectedPlayers.length < minSquadSize) {
        if (!confirm(`You have only ${selectedPlayers.length} players selected. The recommended minimum is ${minSquadSize}. Publish anyway?`)) {
          return;
        }
      }

      if (selectedPlayers.length > maxSquadSize) {
        if (!confirm(`You have ${selectedPlayers.length} players selected, which is more than the tournament limit of ${maxSquadSize}. Publish anyway?`)) {
          return;
        }
      }
    }

    // Show modal if we're publishing without explicit selection yet
    if (isPublish && !overrideNotificationOption) {
      setShowPublishModal(true);
      return;
    }

    // Clear super subs if not allowed
    const finalSuperSubs = allowSuperSubs ? superSubs : [];

    setIsSaving(true);
    try {
      await onSave({
        playing11: selectedPlayers,
        superSubs: finalSuperSubs,
        guestPlayers: guestPlayers,
        playerDuties: playerDuties,
        isPublish: isPublish,
        notificationOption: overrideNotificationOption
      });
      setShowPublishModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  const getDutyLabel = (dutyId) => {
    const duty = AVAILABLE_DUTIES.find(d => d.id === dutyId);
    return duty ? `${duty.icon} ${duty.label}` : 'Assign Duty';
  };

  const getDutyBadge = (dutyId) => {
    const duty = AVAILABLE_DUTIES.find(d => d.id === dutyId);
    return duty ? `${duty.icon} ${duty.label}` : null;
  };

  // Count assigned duties
  const assignedDutiesCount = Object.keys(playerDuties).length;

  const regularPlayerCount = selectedPlayers.length - superSubs.length;
  const isPast = game?.date ? new Date(game.date) < new Date(new Date().setHours(0, 0, 0, 0)) : false;

  const allPlayers = [...availablePlayers, ...guestPlayers]
    .filter(player => {
      // Keep guest players (manually added)
      if (player.isGuest) return true;

      // Keep players who have already been selected for the squad (so they don't disappear)
      if (selectedPlayers.includes(player.id)) return true;

      // Otherwise, only show players who explicitly said they are "available",
      // UNLESS the game is in the past, then show everyone to allow accurate data entry
      return isPast || isPlayerAvailable(player);
    })
    .sort((a, b) => {
      const nameA = a.name || 'Unknown';
      const nameB = b.name || 'Unknown';
      return nameA.localeCompare(nameB);
    });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.duty-dropdown')) {
        setOpenDutyDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-1">
        ✕ Cancel
      </button>

      <div className="bg-white rounded-xl shadow-sm p-8">
        <h2 className="text-2xl font-bold mb-2">Select Playing Squad</h2>
        <p className="text-gray-600 mb-2">{game.name}</p>

        {/* Squad Summary */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-lg font-semibold ${isCustomMode
                ? selectedPlayers.length > 0 ? 'text-green-600' : 'text-orange-600'
                : selectedPlayers.length >= minSquadSize && selectedPlayers.length <= maxSquadSize
                  ? 'text-green-600'
                  : 'text-orange-600'
                }`}>
                Selected: {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''}
                {isCustomMode && selectedPlayers.length > 0 && ' ✓'}
                {!isCustomMode && selectedPlayers.length >= minSquadSize && selectedPlayers.length <= maxSquadSize && ' ✓'}
                {!isCustomMode && selectedPlayers.length > maxSquadSize && ' ⚠️'}
              </p>
              {selectedPlayers.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {regularPlayerCount} regular player{regularPlayerCount !== 1 ? 's' : ''}
                  {allowSuperSubs && superSubs.length > 0 && (
                    <span className="ml-2 text-purple-600 font-medium">
                      + {superSubs.length} super sub{superSubs.length !== 1 ? 's' : ''} ⭐
                    </span>
                  )}
                </p>
              )}
              {assignedDutiesCount > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  📋 {assignedDutiesCount} duty assignment{assignedDutiesCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="text-right text-sm text-gray-600">
              <p className="flex items-center gap-1">
                {ballType === 'tennis' ? '🎾' : '🔴'} {ballType === 'tennis' ? 'Tennis' : 'Leather'} Ball
              </p>
              {isCustomMode ? (
                <p className="text-orange-600 font-medium">∞ Custom Mode</p>
              ) : (
                <>
                  <p>Target: {maxSquadSize} players</p>
                  {allowSuperSubs && (
                    <p className="text-purple-600 font-medium">Max 2 super subs ⭐</p>
                  )}
                </>
              )}
            </div>
          </div>
          {isCustomMode && (
            <div className="mt-2 p-2 bg-orange-100 rounded text-sm text-orange-800">
              ∞ Custom mode: Select any number of players. Expenses will be divided equally among all selected players.
            </div>
          )}
          {!isCustomMode && selectedPlayers.length > maxSquadSize && (
            <div className="mt-2 p-2 bg-orange-100 rounded text-sm text-orange-800">
              ⚠️ You have more players than the tournament limit ({maxSquadSize}). You can still save, but expenses will be divided among all {selectedPlayers.length} players.
            </div>
          )}
        </div>

        {/* Info Box about Super Subs - Only show if allowed */}
        {allowSuperSubs && (
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-start gap-2">
              <Star size={20} className="text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" />
              <div className="text-sm">
                <p className="font-semibold text-purple-900 mb-1">About Super Subs</p>
                <p className="text-purple-800">
                  Super subs pay <span className="font-bold">half the cost</span> of regular players for match fees, meals, and other dividable expenses.
                  You can mark up to 2 players as super subs.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Box about Player Duties */}
        <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex items-start gap-2">
            <ClipboardList size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-orange-900 mb-1">Player Responsibilities</p>
              <p className="text-orange-800">
                Assign duties like <span className="font-bold">ground setup</span>, <span className="font-bold">drinks & snacks</span>, or <span className="font-bold">scoring</span> to selected players.
                Use the dropdown next to each selected player to assign responsibilities.
              </p>
            </div>
          </div>
        </div>

        {/* Add Guest Player Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowAddGuest(!showAddGuest)}
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 text-gray-600 hover:text-green-600 transition-colors"
          >
            <UserPlus size={20} />
            <span className="font-semibold">Add Guest/Trial Player</span>
          </button>
        </div>

        {/* Add Guest Form */}
        {showAddGuest && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Add Guest Player</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Guest player name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="guest@example.com"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddGuest}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  Add Guest
                </button>
                <button
                  onClick={() => {
                    setShowAddGuest(false);
                    setGuestName('');
                    setGuestEmail('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Players List */}
        <div className="space-y-3">
          {allPlayers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No players in team. Add players first.</p>
          ) : (
            allPlayers.map(player => {
              const isSelected = selectedPlayers.includes(player.id);
              const isSuperSub = superSubs.includes(player.id);
              const isGuest = player.isGuest;
              const currentDuty = playerDuties[player.id];
              const isDropdownOpen = openDutyDropdown === player.id;
              const playerIsAvailable = isPlayerAvailable(player);

              return (
                <div
                  key={player.id}
                  className={`p-4 border-2 rounded-lg transition-all relative ${openDutyDropdown === player.id ? 'z-40' : 'z-0'
                    } ${isSelected
                      ? isSuperSub
                        ? 'border-purple-500 bg-purple-50'
                        : isGuest
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <div className="flex justify-between items-center gap-3">
                    {/* Player Info */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        if (isGuest) return;
                        togglePlayer(player.id);
                      }}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">
                          {player.name}
                          {isGuest && <span className="text-yellow-600 text-xs ml-2">👤 Guest</span>}
                        </p>
                        {/* Show availability status */}
                        {playerIsAvailable && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-300">
                            ✓ Available
                          </span>
                        )}
                        {isSuperSub && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                            <Star size={12} fill="currentColor" />
                            Super Sub
                          </span>
                        )}
                        {currentDuty && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            {getDutyBadge(currentDuty)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm truncate text-gray-500">{player.email}</p>
                      {isSuperSub && (
                        <p className="text-xs text-purple-600 mt-1">
                          Pays 50% of regular player costs
                        </p>
                      )}
                    </div>

                    {/* Duty Dropdown - Only show for selected players */}
                    {isSelected && (
                      <div className="relative duty-dropdown">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDutyDropdown(isDropdownOpen ? null : player.id);
                          }}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentDuty
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                          <ClipboardList size={14} />
                          <span className="hidden sm:inline">
                            {currentDuty ? getDutyLabel(currentDuty) : 'Assign Duty'}
                          </span>
                          <ChevronDown size={14} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                            {AVAILABLE_DUTIES.map(duty => (
                              <button
                                key={duty.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  assignDuty(player.id, duty.id);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${currentDuty === duty.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                  } ${duty.id === 'none' ? 'border-b border-gray-100' : ''}`}
                              >
                                <span className="w-5">{duty.icon}</span>
                                <span>{duty.label}</span>
                                {currentDuty === duty.id && <span className="ml-auto">✓</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {/* Super Sub Toggle (only shown if player is selected AND super subs allowed) */}
                      {isSelected && allowSuperSubs && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSuperSub(player.id);
                          }}
                          disabled={!isSuperSub && superSubs.length >= 2}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${isSuperSub
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : superSubs.length >= 2
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          title={
                            isSuperSub
                              ? 'Remove super sub status'
                              : superSubs.length >= 2
                                ? 'Maximum 2 super subs allowed'
                                : 'Mark as super sub'
                          }
                        >
                          <div className="flex items-center gap-1">
                            <Star size={14} fill={isSuperSub ? 'currentColor' : 'none'} />
                            <span className="hidden sm:inline">{isSuperSub ? 'Super Sub' : 'Super Sub'}</span>
                          </div>
                        </button>
                      )}

                      {/* Guest Remove / Regular Select Button */}
                      {isGuest ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveGuest(player.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Remove guest"
                        >
                          <X size={18} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlayer(player.id);
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isSelected
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                        >
                          {isSelected ? 'Remove' : 'Select'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Duties Summary */}
        {assignedDutiesCount > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <ClipboardList size={18} />
              Assigned Duties Summary
            </h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(playerDuties).map(([playerId, dutyId]) => {
                const player = allPlayers.find(p => p.id === playerId);
                const duty = AVAILABLE_DUTIES.find(d => d.id === dutyId);
                if (!player || !duty) return null;
                return (
                  <span
                    key={playerId}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-blue-200 rounded-full text-sm"
                  >
                    <span className="font-medium">{player.name}:</span>
                    <span>{duty.icon} {duty.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Save Button */}
        {availablePlayers.length > 0 && (
          <div className="mt-6 space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all bg-white text-purple-600 border-2 border-purple-600 hover:bg-purple-50 disabled:opacity-50`}
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                onClick={() => handleSave(true)}
                disabled={selectedPlayers.length === 0 || isSaving}
                className={`flex-1 flex flex-col items-center justify-center py-2 rounded-lg font-semibold transition-all ${selectedPlayers.length === 0 || isSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : isCustomMode
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : selectedPlayers.length >= minSquadSize && selectedPlayers.length <= maxSquadSize
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
              >
                {selectedPlayers.length === 0 && <span className="py-1">Select players</span>}
                {selectedPlayers.length > 0 && isCustomMode && (
                  <>
                    <span>Publish Squad</span>
                    <span className="text-xs font-normal opacity-90">({selectedPlayers.length} players)</span>
                  </>
                )}
                {selectedPlayers.length > 0 && !isCustomMode && selectedPlayers.length < minSquadSize && (
                  <>
                    <span>Publish with Warning</span>
                    <span className="text-xs font-normal opacity-90">({minSquadSize - selectedPlayers.length} under limit)</span>
                  </>
                )}
                {selectedPlayers.length > 0 && !isCustomMode && selectedPlayers.length >= minSquadSize && selectedPlayers.length <= maxSquadSize && (
                  <>
                    <span className="flex items-center gap-1">
                      Publish Squad
                    </span>
                    <span className="text-xs font-normal opacity-90">
                      ({regularPlayerCount} players {allowSuperSubs && superSubs.length > 0 ? `+ ${superSubs.length} ⭐` : ''})
                    </span>
                  </>
                )}
                {selectedPlayers.length > 0 && !isCustomMode && selectedPlayers.length > maxSquadSize && (
                  <>
                    <span>Publish with Warning</span>
                    <span className="text-xs font-normal opacity-90">({selectedPlayers.length - maxSquadSize} over limit)</span>
                  </>
                )}
              </button>
            </div>

            {/* WhatsApp Broadcast Button - Only show when squad has players */}
            {selectedPlayers.length >= (isCustomMode ? 1 : minSquadSize) && (
              <a
                href={generateWhatsAppSquadMessage()}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-lg font-semibold transition-all bg-green-500 text-white hover:bg-green-600 flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Share Squad on WhatsApp
              </a>
            )}
          </div>
        )}
      </div>

      {/* Publish Options Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in slide-in-from-bottom-4 duration-200">
            <h3 className="text-xl font-bold mb-1">Squad Notifications</h3>
            <p className="text-gray-600 text-sm mb-6">Who would you like to notify about this published squad?</p>

            <div className="space-y-3 mb-8">
              <label className={`flex p-4 border rounded-xl cursor-pointer transition-colors ${publishOption === 'impacted_only' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="publish_option"
                  value="impacted_only"
                  checked={publishOption === 'impacted_only'}
                  onChange={() => setPublishOption('impacted_only')}
                  className="mt-1 mr-3 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <div className="font-semibold text-gray-900">Impacted Players Only</div>
                  <div className="text-xs text-gray-500 mt-1">Notify only those who were newly added, removed, or assigned new duties. Best for mid-week changes.</div>
                </div>
              </label>

              <label className={`flex p-4 border rounded-xl cursor-pointer transition-colors ${publishOption === 'all_roster' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="publish_option"
                  value="all_roster"
                  checked={publishOption === 'all_roster'}
                  onChange={() => setPublishOption('all_roster')}
                  className="mt-1 mr-3 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <div className="font-semibold text-gray-900">All Interested Players</div>
                  <div className="text-xs text-gray-500 mt-1">Send tailored messages (Selected vs Not Selected) to everyone except those who marked Unavailable. Best for the first draft.</div>
                </div>
              </label>

              <label className={`flex p-4 border rounded-xl cursor-pointer transition-colors ${publishOption === 'silent' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="publish_option"
                  value="silent"
                  checked={publishOption === 'silent'}
                  onChange={() => setPublishOption('silent')}
                  className="mt-1 mr-3 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <div className="font-semibold text-gray-900">Silent Publish</div>
                  <div className="text-xs text-gray-500 mt-1">Update the system quietly without sending any push notifications.</div>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPublishModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(true, publishOption)}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex justify-center items-center"
                disabled={isSaving}
              >
                {isSaving ? 'Publishing...' : 'Confirm Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Generate WhatsApp message with squad and duties
  function generateWhatsAppSquadMessage() {
    const gameDate = formatDate(game.date, {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });

    const formatTimeStr = (timeStr) => {
      if (!timeStr) return null;
      try {
        const [hours, minutes] = timeStr.split(':');
        const d = new Date();
        d.setHours(parseInt(hours, 10), parseInt(minutes, 10));
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch (e) {
        return timeStr;
      }
    };

    const headerTitle = [team?.name, tournament?.name].filter(Boolean).join(' ') || 'SQUAD ANNOUNCEMENT';

    let message = String.fromCodePoint(0x1F3CF) + ' *' + headerTitle + '*\n';
    message += String.fromCodePoint(0x1F4C5) + ' ' + gameDate + '\n';

    let displayOpponent = game.opponent;
    if (displayOpponent && team?.name && displayOpponent.toLowerCase().trim() === team.name.toLowerCase().trim() && game.name) {
      // The opponent field is accidentally set to our own team. Try to extract the real opponent from game.name
      const parts = game.name.split(/(?:\s+vs\s+|\s+-\s+)/i);
      const otherTeam = parts.find(p => p.toLowerCase().trim() !== team.name.toLowerCase().trim());
      if (otherTeam) {
        displayOpponent = otherTeam.trim();
      }
    }

    if (displayOpponent) {
      message += String.fromCodePoint(0x1F19A) + ' vs ' + displayOpponent + '\n';
    } else {
      message += String.fromCodePoint(0x1F19A) + ' ' + (game.name || 'Game') + '\n';
    }

    if (game.venue) message += String.fromCodePoint(0x1F4CD) + ' ' + game.venue + '\n';
    if (game.time) message += String.fromCodePoint(0x23F0) + ' Match Time: ' + (formatTimeStr(game.time) || game.time) + '\n';

    if (game.arrivalTime) {
      message += String.fromCodePoint(0x23F0) + ' Reporting Time: ' + formatTimeStr(game.arrivalTime) + ' (Strict)\n';
    }
    if (game.lateFeeCutoffTime) {
      const lateFee = game.lateFeeAmount || 5;
      message += String.fromCodePoint(0x26A0, 0xFE0F) + ' Late Fee Cutoff: ' + formatTimeStr(game.lateFeeCutoffTime) + ' ($' + lateFee + ' fine for late arrivals)\n';
    }

    message += '\n';
    message += String.fromCodePoint(0x1F465) + ' *Playing Squad:*\n';

    let playerNum = 1;
    selectedPlayers.forEach(playerId => {
      const player = allPlayers.find(p => p.id === playerId);
      if (!player) return;

      const isSuperSub = superSubs.includes(playerId);

      let line = playerNum + '. ' + player.name;
      if (isSuperSub) line += ' ' + String.fromCodePoint(0x2B50);

      message += line + '\n';
      playerNum++;
    });

    if (superSubs.length > 0) {
      message += '\n' + String.fromCodePoint(0x2B50) + ' = Super Sub (50% cost)\n';
    }

    if (assignedDutiesCount > 0) {
      message += '\n' + String.fromCodePoint(0x1F4CB) + ' *Match Responsibilities:*\n';
      Object.entries(playerDuties).forEach(([playerId, dutyId]) => {
        const player = allPlayers.find(p => p.id === playerId);
        const duty = AVAILABLE_DUTIES.find(d => d.id === dutyId);
        if (player && duty && duty.id !== 'none') {
          message += '• ' + duty.icon + ' ' + duty.label + ': ' + player.name + '\n';
        }
      });
    }

    message += '\n' + String.fromCodePoint(0x1F455) + ' Dress Code: Team Colors\n';
    message += String.fromCodePoint(0x1F392) + ' Team Kit: Everyone helps pack up after the match\n';

    const isYorkas =
      (team?.name?.toLowerCase().includes('yorkas')) ||
      (game?.name?.toLowerCase().includes('yorkas')) ||
      (game?.homeTeam?.toLowerCase().includes('yorkas')) ||
      (game?.awayTeam?.toLowerCase().includes('yorkas'));

    if (isYorkas) {
      message += '\nGo Yorkas. All In ' + String.fromCodePoint(0x1F4AA) + ' ' + String.fromCodePoint(0x1F3CF);
    } else {
      message += '\nAll the best team! ' + String.fromCodePoint(0x1F3CF) + String.fromCodePoint(0x1F4AA);
    }

    return 'https://api.whatsapp.com/send?text=' + encodeURIComponent(message);
  }
};

export default SquadSelection;